import './index.scss';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Table, Modal } from 'semantic-ui/semantic-ui-imports';
import { Aggregate, AggregateCategory } from '../../Types/Aggregates';
import AggregateCalcuation from '../AggregateCalculation';

type System = {
  aggregates: Array<Aggregate>;
} & AggregateCategory;

export interface ProductTableProps {
  items: System[];
  categories?: Array<AggregateCategory>;
  onSelect?: (a: Aggregate) => void;
  selectedAggregate?: Aggregate;
}

export interface HelpModalContent {
  title: string;
  content?: any;
}

export const DefaultAggregateCategories: AggregateCategory[] = [
  { name: 'name', key: 'name' },
  { name: 'airflow', key: 'inputs.cooling.ambient.VIn' },
  { name: 'KAP', key: 'kap' },
  { name: 'SFP', key: 'system_data.heating.SFP' },
  { name: 'VGV', key: 'system_data.heating.heat_reclaim' },
  { name: 'COP', key: 'system_data.heating.COP' },
  { name: 'heatingKAP', key: 'system_data.heating.Qc' },
  { name: 'supplyTOUT', key: 'system_data.heating.supply.T_out' },
  { name: 'coolEER', key: 'system_data.cooling.EER' },
  { name: 'coolKAP', key: 'system_data.cooling.Qe' },
  { name: 'coolingTOUT', key: 'system_data.cooling.supply.T_out' },
  { name: 'coolingLP', key: 'system_data.cooling.Lp' },
];

const ProductTable = ({
  items,
  categories,
  onSelect,
  selectedAggregate,
}: ProductTableProps) => {
  const [showModal, setShowModal] = React.useState(false);
  const [activeModal, setActiveModal] = React.useState<HelpModalContent | null>(
    null
  );
  const { t } = useTranslation();

  const triggerModal = (target?: any, description?: string) => {
    setActiveModal({
      title: target,
      content: description || t('common.missing'),
    });
    setShowModal(true);
  };

  return (
    <div className="product-table">
      <Table basic attached="top" padded selectable>
        <Table.Header>
          <Table.Row>
            {categories &&
              categories.map((cat) => (
                <React.Fragment key={`product-cat-${cat.key}`}>
                  <Table.HeaderCell>
                    <span>{t(`productPage.tableHeaders.${cat.name}`)}</span>
                  </Table.HeaderCell>
                </React.Fragment>
              ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {items.map(
            (productGroup) =>
              !!productGroup?.aggregates?.length && (
                <React.Fragment key={productGroup.key}>
                  <Table.Row active>
                    <Table.HeaderCell colSpan="11">
                      <Icon
                        name="info circle"
                        link
                        onClick={() => {
                          triggerModal(
                            productGroup.name,
                            productGroup.description
                          );
                        }}
                      />
                      {productGroup.name}
                    </Table.HeaderCell>
                  </Table.Row>
                  {productGroup.aggregates.map((aggregate: Aggregate) => (
                    <React.Fragment key={aggregate.id}>
                      <AggregateCalcuation
                        aggregate={aggregate}
                        activeAggregate={selectedAggregate}
                        categories={categories}
                        onSelect={(a: Aggregate) => {
                          if (onSelect) onSelect(a);
                          // Don't set activeAggregate here - only commit on Continue button
                        }}
                        loading={!aggregate.calculated}
                      />
                    </React.Fragment>
                  ))}
                </React.Fragment>
              )
          )}
        </Table.Body>
      </Table>
      <Modal
        onClose={() => {
          setShowModal(false);
          setActiveModal(null);
        }}
        open={showModal}
        closeIcon
        className="product-table__modal"
      >
        <Modal.Header className="product-table__modal__header">
          {activeModal?.title}
        </Modal.Header>
        <Modal.Content className="product-table__modal__content">
          {activeModal?.content}
        </Modal.Content>
      </Modal>
    </div>
  );
};

ProductTable.defaultProps = {
  categories: DefaultAggregateCategories,
};

export default ProductTable;
